import { ValidBusinessObject } from '@iotv/iotv-v3-types';

export enum tagCatagory {
  property,
  precedence,
  device,
  entity,
  user,
  programme,
  description,
  date,
}

export interface tagDef {
  catagory: tagCatagory;
  label: string;
}

export type EventBaseAttributes = {
  //unique UUID for the event
  id: string;
  //Time event occurred, this is the main sort index of timestream
  timestamp: number;
  //property which the event is relevant to. Populate this if possible
  propertyId?: string;
  //how important the event is. Lower number =  more important
  precedence: number;
  //event type name. Please ensure that your event type is defined below
  //and that the event additionally contains all the fields expected by that type
  type: string;
  // pk|sk of most important object in the event. This is the secondary sort index of the table.
  // If your event relates to a device, please make this the deviceSk
  subjectSk?: string;
  //appended to hydrated tags, leave blank unless you know what you are doing
  tags?: tagDef[];
};

//generic interface for storing multiple different types of events in a list
export type UnknownEvent = EventBaseAttributes & {
  [k: string]: any;
};

export type HydratedUnknownEvent = UnknownEvent & {
  [k: string]: ValidBusinessObject;
};

export type IrrigatorOperatedEvent = EventBaseAttributes & {
  devEUI: string;
  irrigatorId: string;
  programmeId: string;
  reportedStartTime: number;
  reportedEndTime: number;
  timeOnSecs: number;
  seemsScheduled?: boolean;
  likelyRelevantScheduleIndex?: number;
  offSetSecsFromMostLikelyScheduleIndex?: number;
  startEventId?: string;
  endEventId?: string;
};

export type hydratedIrrigatorOperatedEvent = IrrigatorOperatedEvent & {
  device: ValidBusinessObject;
  irrigator: ValidBusinessObject;
  programme: ValidBusinessObject;
  property?: ValidBusinessObject;
  tags: string[];
};

//event representing that a device has hit the max number of retries for applying a programme in calc state.
//subjectSk should be device sk
export type hitMaxRetriesEvent = EventBaseAttributes & {
  devEUI: string;
  irrigatorId: string;
  incomingProgrammeId: string;
  retryCount: number;
};

export type missedIrrigationCycleEvent = EventBaseAttributes & {
  devEUI: string;
  irrigatorId: string;
  propertyId: string;
  programmeId: string;
  cycleOver: boolean;
  correctStartTime: number;
};

export type unscheduledIrrigationEvent = EventBaseAttributes & {
  devEUI: string;
  irrigatorId: string;
  propertyId: string;
  programmeId: string;
  reportedCycle?: number;
  correctStartTime?: number;
  reportedCycleLastScheduledTime?: number;
  reportedCycleNextScheduledTime?: number;
};

//event representing that a device ignored a downlink and the downlink is being resent.
//subjectSk should be device sk
export type ignoredDownlinkEvent = EventBaseAttributes & {
  devEUI: string;
  irrigatorId: string;
  incomingProgrammeId: string;
  retryCount: number;
};

//event representing that a device ignored a downlink and the downlink is being resent.
//subjectSk should be device sk
export type acknowledgedDownlinkEvent = EventBaseAttributes & {
  devEUI: string;
  irrigatorId: string;
  programmeId: string;
  rawPayload?: string;
  retryCount: number;
};

//event representing that a device has reported an incorrect time.
//subjectSk should be device sk
export type timeDesyncedEvent = EventBaseAttributes & {
  devEUI: string;
  irrigatorId: string;
  desyncSecs: number;
};

//event representing that a user has (re)applied a programme on a property
//subjectSk should be programme sk
export type programmeAppliedEvent = EventBaseAttributes & {
  applyingUserId: string;
  programmeId: string;
  programmeVersionId?: string;
};

//event representing that a user has (re)applied a programme on a property
//subjectSk should be programme sk
export type programmeUnappliedEvent = EventBaseAttributes & {
  applyingUserId: string;
  programmeId: string;
  programmeVersionId?: string;
};

export type hydratedProgrammeAppliedEvent = programmeAppliedEvent & {
  applyingUser: ValidBusinessObject;
  currentProgramme: ValidBusinessObject;
  programmeAtApplyTime?: ValidBusinessObject;
};

export type hydratedHitMaxRetriesEvent = hitMaxRetriesEvent & {
  device: ValidBusinessObject;
  irrigator: ValidBusinessObject;
  property: ValidBusinessObject;
  incomingProgramme: ValidBusinessObject;
  tags: string[];
};

export type SQMBaseEvent = EventBaseAttributes & {
  isPayloadSent: boolean;
  devEUI: string | undefined;
  downlinkPayload: string | undefined;
  irrigatorId?: string;
};

export type SQMApprovalEvent = SQMBaseEvent & {
  isFlushed: boolean;
  flushedPayload?: string;
  isRequeued: boolean;
  requeuedPayload?: string;
};

export type SQMRejectEvent = SQMBaseEvent & {
  reason: string;
};

export type SQMUnhandledEvent = SQMBaseEvent & {
  suggestion: string;
};
